<template>
  <div
    v-touch.long="() => emit('long-press')"
    class="group relative flex cursor-pointer items-start rounded-8 p-1 hover:bg-bg-base-bg3 active:bg-surf-neut-soft-pres"
    :data-gallery-media-anchor="!showKebab ? anchorId : undefined"
    data-test="chat-gallery-video-item"
    @click="emit('open')"
    @contextmenu.prevent="emit('contextmenu', $event)"
  >
    <div class="relative h-22.5 w-37 shrink-0 overflow-hidden rounded-4 bg-surf-neut-soft-def">
      <video
        v-if="previewSrc"
        :src="previewSrc"
        class="pointer-events-none h-full w-full object-cover"
        muted
        playsinline
        preload="metadata"
        @loadedmetadata="onVideoMetadata"
      />
      <span
        v-if="durationLabel"
        class="absolute bottom-1 right-1 rounded-full bg-surf-lock-alpha-brand-blk-40 px-1.5 text-label-r-12-18 text-surf-lock-wht"
        data-test="chat-gallery-video-duration"
      >
        {{ durationLabel }}
      </span>
    </div>

    <div class="ml-3 flex min-w-0 flex-1 flex-col gap-1 py-0.5">
      <p
        class="line-clamp-2 break-words text-body-m-14-20 text-fg-base-fg3"
        :title="title"
      >
        {{ title }}
      </p>
      <p
        v-if="!isEventRecording && senderName"
        class="truncate text-body-r-14-20 text-fg-base-fg14"
        data-test="chat-gallery-video-sender"
      >
        {{ senderName }}
      </p>
      <div class="flex min-w-0 items-center gap-1">
        <wui-icon
          v-if="isEventRecording"
          name="recordM"
          class="size-4! shrink-0 text-fg-neg-def"
          data-test="chat-gallery-video-recording-icon"
        />
        <p class="truncate text-label-r-13-18 text-fg-base-fg22">
          {{ metaLabel }}
        </p>
      </div>
    </div>

    <div
      v-if="showKebab"
      class="ml-1 hidden shrink-0 self-start group-hover:block"
      :class="{ '!block': isMenuActive }"
    >
      <wui-btn
        icon
        variant="neut"
        state="alpha"
        :size="32"
        prepend-icon="kebabVerticalM"
        :data-gallery-media-anchor="anchorId"
        data-test="chat-gallery-video-menu"
        :aria-label="$t('common.more')"
        @click.stop="emit('open-menu', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { WuiBtn, WuiIcon } from '@wui/common-library'
import { computed, ref } from 'vue'

import { vTouch } from '@/shared/directives/v-touch'

const props = withDefaults(defineProps<{
  title: string
  senderName?: string
  metaLabel: string
  previewSrc?: string
  durationSeconds?: number
  isEventRecording?: boolean
  showKebab?: boolean
  isMenuActive?: boolean
  anchorId?: string
}>(), {
  senderName: '',
  previewSrc: '',
  durationSeconds: 0,
  isEventRecording: false,
  showKebab: false,
  isMenuActive: false,
  anchorId: '',
})

const emit = defineEmits<{
  (event: 'open'): void
  (event: 'open-menu', mouseEvent: Event): void
  (event: 'contextmenu', mouseEvent: MouseEvent): void
  (event: 'long-press'): void
}>()

const loadedDurationSeconds = ref<number | undefined>(undefined)

const formatDuration = (totalSeconds: number) => {
  const seconds = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const restSeconds = seconds % 60
  const mm = String(minutes).padStart(hours > 0 ? 2 : 1, '0')
  const ss = String(restSeconds).padStart(2, '0')

  if (hours > 0) {
    return `${hours}:${mm.padStart(2, '0')}:${ss}`
  }

  return `${minutes}:${ss}`
}

const durationLabel = computed(() => {
  const value = props.durationSeconds > 0
    ? props.durationSeconds
    : loadedDurationSeconds.value
  if (value === undefined || !Number.isFinite(value) || value <= 0) {
    return ''
  }

  return formatDuration(value)
})

const onVideoMetadata = (event: Event) => {
  const video = event.target as HTMLVideoElement
  if (!Number.isFinite(video.duration) || video.duration <= 0) {
    return
  }

  loadedDurationSeconds.value = video.duration
  if (video.currentTime === 0) {
    video.currentTime = 0.001
  }
}
</script>
